<template>
  <main class="auth-form-page">
    <AppNavBar :title="t('auth.changePassword')" :border="false" />

    <section class="auth-form-section">
      <label class="auth-form-label">
        {{ t("auth.currentPassword") }} <em>*</em>
      </label>
      <AppMaskedPasswordField
        v-model="currentPassword"
        class="auth-form-field"
        :visible="showCurrentPassword"
        clearable
        :placeholder="t('auth.enterCurrentPassword')"
      >
        <template #button>
          <button
            type="button"
            class="auth-eye-btn"
            @click="showCurrentPassword = !showCurrentPassword"
          >
            <app-image
              :src="showCurrentPassword ? eyeOpenIcon : eyeCloseIcon"
              alt=""
            />
          </button>
        </template>
      </AppMaskedPasswordField>
    </section>

    <section class="auth-form-section">
      <label class="auth-form-label">
        {{ t("auth.setPassword") }} <em>*</em>
      </label>
      <AppMaskedPasswordField
        v-model="newPassword"
        class="auth-form-field"
        :visible="showNewPassword"
        clearable
        :placeholder="t('auth.pleaseSetNewPasswordMin8')"
      >
        <template #button>
          <button
            type="button"
            class="auth-eye-btn"
            @click="showNewPassword = !showNewPassword"
          >
            <app-image
              :src="showNewPassword ? eyeOpenIcon : eyeCloseIcon"
              alt=""
            />
          </button>
        </template>
      </AppMaskedPasswordField>
    </section>

    <section class="auth-form-section">
      <label class="auth-form-label">
        {{ t("auth.confirmNewPassword") }} <em>*</em>
      </label>
      <AppMaskedPasswordField
        v-model="newPasswordAgain"
        class="auth-form-field"
        :visible="showNewPasswordAgain"
        :class="{
          'auth-form-field--error':
            newPasswordAgain && newPasswordAgain !== newPassword,
        }"
        clearable
        :placeholder="t('auth.setNewPasswordAgain')"
      >
        <template #button>
          <button
            type="button"
            class="auth-eye-btn"
            @click="showNewPasswordAgain = !showNewPasswordAgain"
          >
            <app-image
              :src="showNewPasswordAgain ? eyeOpenIcon : eyeCloseIcon"
              alt=""
            />
          </button>
        </template>
      </AppMaskedPasswordField>
    </section>

    <section class="agreement-row">
      <!-- <van-checkbox
        v-model="agreed"
        checked-color="var(--wp-color-theme)"
        icon-size="16px"
      /> -->
      <AppSvgIcon
        @click="agreed = !agreed"
        v-if="agreed"
        name="icon"
        size="16"
        :color="agreed ? 'var(--wp-color-theme)' : ''"
      />
      <AppSvgIcon
        @click="agreed = !agreed"
        v-if="!agreed"
        name="icon-1"
        size="16"
        color="var(--wp-color-theme)"
      />
      <p>
        {{ t("auth.passwordOnlySavedLocally1") }}
        <span>{{ t("auth.passwordOnlySavedLocally2") }}</span>
      </p>
    </section>

    <AuthThemeButton
      block
      class="auth-submit-btn"
      :loading="submitting"
      :disabled="!canSubmit"
      @click="submitChange"
    >
      {{ t("common.confirm") }}
    </AuthThemeButton>

    <van-popup v-model:show="showSuccessPopup" round class="success-popup">
      <section class="success-dialog">
        <button
          type="button"
          class="success-dialog__close"
          @click="handleSuccessDone"
        >
          <van-icon name="cross" />
        </button>
        <app-image :src="successPopupImg" alt="" class="success-dialog__icon" />
        <div data-strong>{{ t("auth.changeSuccess") }}</div>
        <van-button block round type="primary" @click="handleSuccessDone">
          {{ t("card.reviewDone") }}
        </van-button>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useThemeStore } from "@/stores/theme";
import { useRouter } from "vue-router";
import { changePayPwd } from "@/api/auth";
import AuthThemeButton from "@/view/auth/components/AuthThemeButton.vue";
import AppMaskedPasswordField from "@/components/AppMaskedPasswordField.vue";
import { useProfileStore } from "@/stores/profile";
import { getWalletPassword, saveWalletPassword } from "@/utils/walletSession";
import eyeOpenIcon from "@/assets/images/eye_open_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close_icon.png";
import successPopupIcon from "@/assets/images/SVG/img11.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const themeStore = useThemeStore();

const currentPassword = ref("");
const newPassword = ref("");
const newPasswordAgain = ref("");
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showNewPasswordAgain = ref(false);
const submitting = ref(false);
const agreed = ref(false);
const showSuccessPopup = ref(false);

const canSubmit = computed(
  () =>
    !!currentPassword.value.trim() &&
    !!newPassword.value.trim() &&
    !!newPasswordAgain.value.trim() &&
    agreed.value,
);

const successPopupImg = computed(() =>
  themeStore.image("img11", successPopupIcon),
);

function validate() {
  if (!currentPassword.value.trim()) {
    return t("auth.enterCurrentPassword");
  }
  if (currentPassword.value.trim().length < 8) {
    return t("auth.pleaseSetNewPasswordMin8");
  }

  if (newPassword.value.trim().length < 8) {
    return t("auth.pleaseSetNewPasswordMin8");
  }
  if (newPassword.value.trim() === currentPassword.value.trim()) {
    return t("auth.newPasswordSameAsOld");
  }
  if (!newPasswordAgain.value.trim()) {
    return t("auth.setNewPasswordAgain");
  }
  if (newPassword.value !== newPasswordAgain.value) {
    return t("auth.passwordsNotMatch");
  }
  if (!agreed.value) {
    return `${t("auth.passwordOnlySavedLocally1")}${t("auth.passwordOnlySavedLocally2")}`;
  }
  return "";
}

async function submitChange() {
  const message = validate();
  if (message) {
    showFailToast(message);
    return;
  }

  const savedPassword = getWalletPassword(profileStore.accountStorageAddress);
  if (savedPassword && savedPassword !== currentPassword.value.trim()) {
    showFailToast(t("auth.currentPasswordIncorrect"));
    return;
  }

  submitting.value = true;
  try {
    await changePayPwd({
      payPwd: newPassword.value.trim(),
      oldPwd: currentPassword.value.trim(),
    });
    if (profileStore.accountStorageAddress) {
      saveWalletPassword(
        profileStore.accountStorageAddress,
        newPassword.value.trim(),
      );
    }
    showSuccessPopup.value = true;
  } catch (_) {
    // handled in interceptor
  } finally {
    submitting.value = false;
  }
}

function handleSuccessDone() {
  showSuccessPopup.value = false;
  router.back();
}
</script>

<style scoped lang="scss">
@use "@/styles/auth-form.scss";

.agreement-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 32px;

  p {
    margin: 0;
    color: #666666;
    font-size: 12px;
    line-height: 18px;
  }

  span {
    color: #e90026;
  }
}

.success-popup {
  width: calc(100% - 48px);
  max-width: 320px;
  overflow: visible;
  background: transparent;
}

.success-dialog {
  position: relative;
  padding: 18px 20px 20px;
  border-radius: 16px;
  background: #ffffff;
  text-align: center;

  [data-strong] {
    display: block;
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.85);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  :deep(.van-button) {
    height: 44px;
    margin-top: 28px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 10px;
  }
}
.auth-submit-btn {
  margin-top: 10px;
}

.success-dialog__close {
  position: absolute;
  top: 14px;
  right: 14px;
  border: 0;
  background: transparent;
  color: #999999;
  font-size: 18px;
}

.success-dialog__icon {
  display: block;
  width: 72px;
  height: 72px;
  margin: 34px auto 0;
  object-fit: contain;
}
</style>
